<template>
  <div class="dossier-ident">
    <b-overlay :show="loading" opacity="0.99">
      <!-- ─── Página simulada ─── -->
      <div class="dossier-ident__page" ref="identPage">
        <!-- Logotipo no cabeçalho de todas as páginas internas -->
        <div class="dossier-ident__page-header">
          <img
            v-if="institution.logo_url"
            :src="institution.logo_url"
            class="dossier-ident__page-logo"
            alt="Logotipo"
          />
          <div v-else class="dossier-ident__page-logo-placeholder">
            <i class="fas fa-building"></i>
          </div>
        </div>

        <!-- Título da seção -->
        <h2 class="dossier-ident__section-title">DADOS DA INSTITUIÇÃO</h2>

        <!-- ── Bloco de dados da empresa ── -->
        <div class="dossier-ident__data-grid">
          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">Razão Social</span>
            <span class="dossier-ident__data-value">{{
              institution.name || "—"
            }}</span>
          </div>

          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">Nome Fantasia</span>
            <span class="dossier-ident__data-value">{{
              institution.fantasy_name || "—"
            }}</span>
          </div>

          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">CNPJ</span>
            <span class="dossier-ident__data-value">{{
              formatCnpj(institution.cnpj) || "—"
            }}</span>
          </div>

          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">Endereço</span>
            <span class="dossier-ident__data-value">{{ fullAddress }}</span>
          </div>

          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">Município / UF</span>
            <span class="dossier-ident__data-value">
              {{ institution.city || "—" }}
              <template v-if="institution.uf"> / {{ institution.uf }}</template>
            </span>
          </div>

          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">Telefone</span>
            <span class="dossier-ident__data-value">{{
              institution.phone || "—"
            }}</span>
          </div>

          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">E-mail institucional</span>
            <span class="dossier-ident__data-value">{{
              institution.email || "—"
            }}</span>
          </div>

          <!-- TODO: Natureza jurídica — campo a ser adicionado no cadastro da empresa -->
          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label">Natureza jurídica</span>
            <span
              v-if="institution.legal_nature"
              class="dossier-ident__data-value"
            >
              {{ institution.legal_nature }}
            </span>
            <span
              v-else
              class="dossier-ident__data-value dossier-ident__data-value--todo"
            >
              <!-- Exibe no modo edição o aviso de campo pendente -->
              <template v-if="!isViewMode">
                <i class="fas fa-tools mr-1 dossier-ident__todo-icon"></i>
                Campo pendente — adicionar ao cadastro da empresa
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <!-- TODO: Data de início das atividades -->
          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label"
              >Data de início das atividades</span
            >
            <span
              v-if="institution.activities_start_date"
              class="dossier-ident__data-value"
            >
              {{ formatDateBR(institution.activities_start_date) }}
              <span v-if="yearsOfOperation" class="dossier-ident__data-age">
                ({{ yearsOfOperation }})
              </span>
            </span>
            <span
              v-else
              class="dossier-ident__data-value dossier-ident__data-value--todo"
            >
              <template v-if="!isViewMode">
                <i class="fas fa-tools mr-1 dossier-ident__todo-icon"></i>
                Campo pendente — adicionar ao cadastro da empresa
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <!-- TODO: Capacidade declarada de acolhimento -->
          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label"
              >Capacidade declarada de acolhimento</span
            >
            <span
              v-if="institution.declared_capacity"
              class="dossier-ident__data-value dossier-ident__data-value--highlight"
            >
              {{ institution.declared_capacity }} residentes
            </span>
            <span
              v-else
              class="dossier-ident__data-value dossier-ident__data-value--todo"
            >
              <template v-if="!isViewMode">
                <i class="fas fa-tools mr-1 dossier-ident__todo-icon"></i>
                Campo pendente — adicionar ao cadastro da empresa
              </template>
              <template v-else>—</template>
            </span>
          </div>
        </div>

        <!-- ── Bloco de residentes (busca automática) ── -->
        <div class="dossier-ident__residents-block">
          <div class="dossier-ident__data-row">
            <span class="dossier-ident__data-label"
              >Número de residentes ativos</span
            >
            <span
              class="dossier-ident__data-value dossier-ident__data-value--highlight"
            >
              {{ totalResidents }} residente{{
                totalResidents !== 1 ? "s" : ""
              }}
            </span>
          </div>

          <!-- Perfil por grau e sexo -->
          <div class="dossier-ident__data-row dossier-ident__data-row--profile">
            <span class="dossier-ident__data-label"
              >Número de residentes por grau e sexo</span
            >
            <div class="dossier-ident__profile-grid">
              <template v-if="loadingResidents">
                <span class="dossier-ident__data-value">
                  <i class="fas fa-spinner fa-spin mr-1"></i>Carregando...
                </span>
              </template>
              <template v-else>
                <div
                  v-for="profile in residentProfile"
                  :key="profile.grade"
                  class="dossier-ident__profile-row"
                >
                  <span class="dossier-ident__profile-grade">{{
                    profile.grade
                  }}</span>
                  <span class="dossier-ident__profile-counts">
                    <template v-if="profile.female > 0">
                      {{ profile.female }} Feminino{{
                        profile.female !== 1 ? "s" : ""
                      }}
                    </template>
                    <template v-if="profile.female > 0 && profile.male > 0"
                      >;
                    </template>
                    <template v-if="profile.male > 0">
                      {{ profile.male }} Masculino{{
                        profile.male !== 1 ? "s" : ""
                      }}
                    </template>
                    <template v-if="profile.female === 0 && profile.male === 0">
                      —
                    </template>
                  </span>
                </div>
                <div
                  v-if="residentProfile.length === 0"
                  class="dossier-ident__data-value"
                >
                  Nenhum residente ativo
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- ── Texto sobre o Dossiê ── -->
        <div class="dossier-ident__about-dossier">
          <h3 class="dossier-ident__about-title">
            Sobre o Dossiê Institucional
          </h3>
          <p class="dossier-ident__about-text">
            O Dossiê Institucional é um conjunto organizado de informações e
            documentos da ILPI, destinado a apoiar a gestão interna e a
            apresentação à fiscalização dos órgãos competentes.
          </p>
          <p class="dossier-ident__about-text">
            Os documentos aqui reunidos refletem a realidade institucional da
            ILPI e sua forma de organização, respeitando a legislação vigente e
            a natureza da instituição como serviço da assistência social.
          </p>
          <p class="dossier-ident__about-text">
            O Sistema Clin atua exclusivamente como ferramenta de organização,
            registro e acesso às informações, não substituindo obrigações legais
            nem validando o conteúdo dos documentos apresentados.
          </p>
        </div>
      </div>

      <!-- ─── Avisos de TODO (fora da página impressa) ─── -->
      <div v-if="!isViewMode && !loading" class="dossier-ident__todos">
        <div class="dossier-ident__todo-block">
          <i class="fas fa-tools mr-1"></i>
          <strong>Campos pendentes — alinhamento com Luiz:</strong>
          <ul class="dossier-ident__todo-list">
            <li>
              <strong>Natureza jurídica</strong> — adicionar ao cadastro da
              empresa com opções: Privada com fins lucrativos / Privada sem fins
              lucrativos / Filantrópica / Pública
            </li>
            <li>
              <strong>Data de início das atividades</strong> — adicionar ao
              cadastro da empresa (calculará tempo de fundação automaticamente)
            </li>
            <li>
              <strong>Capacidade declarada de acolhimento</strong> — adicionar
              ao cadastro da empresa (diferente da capacidade autorizada da
              Infraestrutura)
            </li>
          </ul>
          Enquanto esses campos não forem adicionados, as linhas correspondentes
          ficam visíveis com o aviso acima somente no modo de preenchimento. No
          modo de visualização exibem "—".
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import api from "../../services/api";
import { userData } from "../../lib/functions";

export default {
  name: "DossierIdentification",

  props: {
    mode: {
      type: String,
      default: "view",
      validator: (v) => ["edit", "view"].includes(v),
    },
  },

  data() {
    return {
      loading: true,
      loadingResidents: true,

      institution: {
        name: "",
        fantasy_name: "",
        cnpj: "",
        address: "",
        number_address: "",
        complement: "",
        neighborhood: "",
        city: "",
        uf: "",
        phone: "",
        email: "",
        logo_url: null,
        // TODO: campos a adicionar ao cadastro
        legal_nature: null,
        activities_start_date: null,
        declared_capacity: null,
      },

      // Dados de residentes — buscados na geração do documento
      totalResidents: 0,
      residentProfile: [],
      // Formato esperado de residentProfile:
      // [
      //   { grade: 'Grau I',   female: 6, male: 4 },
      //   { grade: 'Grau II',  female: 12, male: 9 },
      //   { grade: 'Grau III', female: 1, male: 0 },
      // ]
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    fullAddress() {
      const parts = [
        this.institution.address,
        this.institution.number_address,
        this.institution.complement,
        this.institution.neighborhood,
      ].filter(Boolean);
      return parts.length ? parts.join(", ") : "—";
    },

    // Calcula anos de operação a partir da data de início
    yearsOfOperation() {
      const start = this.institution.activities_start_date;
      if (!start) return null;
      const startDate = new Date(start);
      const today = new Date();
      const years = today.getFullYear() - startDate.getFullYear();
      const months = today.getMonth() - startDate.getMonth();
      const totalMonths = years * 12 + months;
      const y = Math.floor(totalMonths / 12);
      const m = totalMonths % 12;
      const parts = [];
      if (y > 0) parts.push(`${y} ano${y !== 1 ? "s" : ""}`);
      if (m > 0) parts.push(`${m} ${m !== 1 ? "meses" : "mês"}`);
      return parts.length ? parts.join(" e ") : null;
    },
  },

  created() {
    this.retrieve();
    this.loadResidentProfile();
  },

  methods: {
    // ── Dados da empresa ───────────────────────────────────────────────────
    async retrieve() {
      try {
        this.loading = true;

        const { user } = userData();
        const response = await api.get("/rest-home/dossier/institution");
        const { error, institution } = response.data;

        if (!error && institution) {
          // Mapeia todos os campos disponíveis
          Object.keys(this.institution).forEach((key) => {
            if (institution[key] !== undefined) {
              this.institution[key] = institution[key];
            }
          });

          // Fallback de logo e nome via sessão
          if (!this.institution.logo_url)
            this.institution.logo_url = user?.logo_url || null;
          if (!this.institution.name)
            this.institution.name = user?.rest_home_name || "";
        } else {
          // Fallback total via sessão
          if (user) {
            this.institution.name = user.rest_home_name || "";
            this.institution.logo_url = user.logo_url || null;
          }
        }
      } catch (err) {
        console.error("DossierIdentification.retrieve:", err);
      } finally {
        this.loading = false;
      }
    },

    // ── Perfil de residentes (busca na hora de gerar — sempre atualizado) ──
    async loadResidentProfile() {
      try {
        this.loadingResidents = true;
        const response = await api.get("/rest-home/dossier/resident-profile");
        const { error, total, profile } = response.data;

        if (!error) {
          this.totalResidents = total || 0;
          this.residentProfile = profile || [];
        }
      } catch (err) {
        console.error("DossierIdentification.loadResidentProfile:", err);
      } finally {
        this.loadingResidents = false;
      }
    },

    // ── Utilitários ────────────────────────────────────────────────────────
    formatCnpj(cnpj) {
      if (!cnpj) return "";
      const digits = String(cnpj).replace(/\D/g, "");
      if (digits.length !== 14) return cnpj;
      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER IDENTIFICAÇÃO INSTITUCIONAL
   ═══════════════════════════════════════════════════════════════ */

.dossier-ident {
  width: 100%;
}

/* ── Página simulada ─────────────────────────────────────────── */
.dossier-ident__page {
  width: 100%;
  max-width: 794px;
  margin: 0 auto;
  padding: 32px 56px 48px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  font-family: "WorkSans", Arial, sans-serif;
  color: #222;
}

/* ── Cabeçalho de página (logo lateral esquerda) ─────────────── */
.dossier-ident__page-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
}

.dossier-ident__page-logo {
  height: 60px;
  max-width: 200px;
  object-fit: contain;
}

.dossier-ident__page-logo-placeholder {
  width: 60px;
  height: 60px;
  background: #f0f0ef;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #bbb;
}

/* ── Título da seção ─────────────────────────────────────────── */
.dossier-ident__section-title {
  font-size: 22px;
  font-weight: 700;
  color: #1f5a76;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 24px;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
}

/* ── Grid de dados ───────────────────────────────────────────── */
.dossier-ident__data-grid,
.dossier-ident__residents-block {
  width: 100%;
  margin-bottom: 20px;
}

.dossier-ident__data-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f4f4f4;
  flex-wrap: wrap;
}

.dossier-ident__data-row--profile {
  align-items: flex-start;
}

.dossier-ident__data-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  min-width: 220px;
}

.dossier-ident__data-value {
  font-size: 12px;
  color: #222;
  flex: 1;
}

.dossier-ident__data-value--highlight {
  font-size: 20px;
  font-weight: 700;
  color: #1f5a76;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
}

.dossier-ident__data-value--todo {
  font-size: 11px;
  color: #856404;
  font-style: italic;
}

.dossier-ident__todo-icon {
  color: #856404;
}

.dossier-ident__data-age {
  font-size: 12px;
  color: #888;
  margin-left: 6px;
}

/* ── Perfil de residentes ────────────────────────────────────── */
.dossier-ident__profile-grid {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.dossier-ident__profile-row {
  display: flex;
  gap: 10px;
  font-size: 12px;
}

.dossier-ident__profile-grade {
  font-weight: 600;
  color: #1f5a76;
  min-width: 60px;
}

.dossier-ident__profile-counts {
  color: #333;
}

/* ── Texto sobre o Dossiê ────────────────────────────────────── */
.dossier-ident__about-dossier {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.dossier-ident__about-title {
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dossier-ident__about-text {
  font-size: 12px;
  color: #555;
  margin: 0 0 6px;
  line-height: 1.6;
}

/* ── Avisos de TODO ──────────────────────────────────────────── */
.dossier-ident__todos {
  max-width: 794px;
  margin: 12px auto 0;
}

.dossier-ident__todo-block {
  font-size: 12px;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 12px 14px;
  line-height: 1.6;
}

.dossier-ident__todo-list {
  margin: 6px 0 8px 16px;
  padding: 0;
}

.dossier-ident__todo-list li {
  margin-bottom: 4px;
}

/* ── Impressão ───────────────────────────────────────────────── */
@media print {
  .dossier-ident__todos {
    display: none;
  }
  .dossier-ident__page {
    box-shadow: none;
    border: none;
  }
}
</style>